import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { EstimateurBudgetSelection } from '@components/estimateur-budget-selection/models';
import { EstimateOptionsPipe } from '@components/estimateur-budget-selection/pipes';
import { EstimatorOption } from '@components/estimateur-etape/models';
import { PopinEstimationEmailComponent } from '@components/popin-estimation-email';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { OptionCardSelectComponent } from '@shared/components/option-card-select';
import { SvgComponent } from '@shared/components/svg';
import { GtmEvent } from '@shared/services/data-layer/models';
import { DialogService } from '@shared/services/dialog';
import { LogsService } from '@shared/services/logs';
import { BudgetEstimatorStore, CommonStore } from '@stores';
import { BudgetEstimatorEstimate } from '@stores/models';

@Component({
  selector: 'app-estimateur-budget-selection',
  imports: [ButtonComponent, SvgComponent, TranslatePipe, EstimateOptionsPipe, CurrencyPipe, OptionCardSelectComponent],
  templateUrl: './estimateur-budget-selection.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EstimateurBudgetSelectionComponent extends AbstractDynamicComponent<EstimateurBudgetSelection> {
  readonly budgetEstimatorUrl = inject(CommonStore).select('budgetEstimatorUrl');
  protected readonly hasSeveralHouseholdAppliances = computed(
    () => (this.currentEstimate()?.householdApplianceSelected ?? []).length > 1
  );
  protected readonly currentEstimateHouseholdAppliancesNames = computed(() => {
    return this.currentEstimate()?.householdApplianceSelected?.map((appliance) => appliance.name);
  });
  protected readonly currentEstimateWithoutHouseholdAppliances = computed(() => {
    return {
      ...this.currentEstimate(),
      householdApplianceSelected: [],
    };
  });
  protected readonly currentEstimateFirstHouseholdAppliance = computed(
    () => this.currentEstimate()?.householdApplianceSelected?.[0]
  );
  protected readonly recentEstimatesProcessed = computed(() => {
    const recent = this.recentEstimates()?.slice(-3) ?? [];
    return recent.map((estimate) => ({
      original: estimate,
      hasSeveralHouseholdAppliances: (estimate.householdApplianceSelected ?? []).length > 1,
      householdAppliancesNames: estimate.householdApplianceSelected?.map((appliance) => appliance.name) ?? [],
      withoutHouseholdAppliances: {
        ...estimate,
        householdApplianceSelected: [],
      },
      firstHouseholdAppliance: estimate.householdApplianceSelected?.[0],
    }));
  });
  private readonly budgetEstimatorStore = inject(BudgetEstimatorStore);
  readonly currentEstimate = this.budgetEstimatorStore.select('currentEstimate');
  readonly recentEstimates = this.budgetEstimatorStore.select('recentEstimates');
  private readonly dialogService = inject(DialogService);
  private readonly router = inject(Router);
  private readonly logs = inject(LogsService);

  redirectToEditPage(option: EstimatorOption, estimateId: string | null = null): void {
    const id = estimateId ?? this.budgetEstimatorStore.select('currentEstimate')()?._id ?? '';
    const recentEstimates = this.recentEstimates() ?? [];
    const targetEstimate = recentEstimates.find((estimate) => estimate._id === id);
    const gtm = this.data.gtmConfigEditButton;
    this.emitGtmEvent({
      event: gtm?.event,
      group: gtm?.group,
      description: gtm?.description,
      context: `${gtm?.context} ${option.name}`,
      button_text_value: 'Modifier',
      event_type: 'click',
    });
    void this.router
      .navigate([this.budgetEstimatorUrl() ?? ''], {
        queryParams: {
          step: option._step,
          estimate_id: id,
        },
      })
      .then(() => {
        if (targetEstimate) {
          this.budgetEstimatorStore.update('currentEstimate', targetEstimate);
        }
      })
      .catch((err: unknown) =>
        this.logs.error('EstimateurBudgetSelectionComponent', 'Navigation to estimate edit page failed', err)
      );
  }

  onNewEstimationClick(btnLabel: string): void {
    const gtm = this.data.gtmConfigCreateEstimate;
    this.emitGtmEvent({
      event: gtm?.event,
      group: gtm?.group,
      description: gtm?.description,
      context: gtm?.context,
      button_text_value: btnLabel,
      event_type: 'click',
    });
    const recentEstimates = this.recentEstimates() ?? [];
    this.budgetEstimatorStore.set({ recentEstimates });
    void this.router.navigate([this.budgetEstimatorUrl() ?? '']);
  }

  onSendEstimationClick(estimate: BudgetEstimatorEstimate, btnLabel: string): void {
    const gtmBtnSendEstimateByEmail = this.data.gtmConfigSendEstimateByEmail;
    const gtmEstimateEmailSent = this.data.gtmConfigEstimateEmailSent;
    this.emitGtmEvent({
      event: gtmBtnSendEstimateByEmail?.event,
      group: gtmBtnSendEstimateByEmail?.group,
      description: gtmBtnSendEstimateByEmail?.description,
      context: gtmBtnSendEstimateByEmail?.context,
      button_text_value: btnLabel,
      event_type: 'click',
    });
    const gtmEventForEmailSent: Partial<GtmEvent> = {
      event: gtmEstimateEmailSent?.event,
      group: gtmEstimateEmailSent?.group,
      description: gtmEstimateEmailSent?.description,
      context: gtmEstimateEmailSent?.context,
      button_text_value: btnLabel,
      event_type: 'submit',
    };
    this.dialogService.openDialog(PopinEstimationEmailComponent, 'SMALL', {
      data: {
        estimate,
        gtmEventForEmailSent,
      },
    });
  }
}
